import { IconButton } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CreateStackLine,
  LinkLine,
  MoreHorizontalLine,
} from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Select_StartPageAction } from "@mirage/analytics/events/types/select_start_page_action";
import { SummarizeAction } from "@mirage/mosaics/SummaryQnaPanel/SummarizeAction";
import { ChatEntryPoint } from "@mirage/mosaics/SummaryQnaPanel/SummaryQna";
import { SummaryQnAEnum } from "@mirage/mosaics/SummaryQnaPanel/utils";
import { publishEvent, SurveyEvent } from "@mirage/service-feedback";
import { copyToClipboard } from "@mirage/service-platform-actions";
import { showSnackbar } from "@mirage/shared/snackbar";
import { useShowStackChooserModal } from "@mirage/stacks/StackChooser/StackChooser";
import i18n from "@mirage/translations";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import type { dash_feed, stacks } from "@dropbox/api-v2-client";
import { Chip } from "@dropbox/dig-components/chip";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { LastViewedStackInfo } from "@mirage/service-recent-content/types";
import { shouldHighlightCompanyPinnedStack } from "@mirage/stacks/Helpers/Utils";

type ActivityActionBarProps = {
  activity: dash_feed.FeedItem;
  index?: number;
  lastViewedInfo?: LastViewedStackInfo;
  companyStackInfo?: Record<string, stacks.PublishData>;
};

export const ActivityActionBar = ({
  activity,
  index,
  lastViewedInfo,
  companyStackInfo,
}: ActivityActionBarProps) => {
  const highlightCompanyStackEnabled = convertFeatureValueToBool(
    useFeatureFlagValue(
      "dash_web_2025_04_10_company_pinned_stack_notification",
    ),
  );
  if (!activity.target) {
    return null;
  }

  const objectDetails = activity.target.details?.object_details;
  const isStackActivity =
    objectDetails?.[".tag"] == "full_stack" && !!objectDetails.stack_data;
  const publishData =
    isStackActivity && objectDetails?.namespace_id
      ? companyStackInfo?.[objectDetails?.namespace_id] ||
        objectDetails?.publish_data
      : undefined;

  return (
    <Box>
      {isStackActivity &&
        !!publishData &&
        objectDetails.namespace_id &&
        highlightCompanyStackEnabled &&
        shouldHighlightCompanyPinnedStack(
          publishData,
          lastViewedInfo?.[objectDetails.namespace_id],
        ) && (
          <Chip size="small" variant={"attention"}>
            <Chip.Content>{i18n.t("new_chip")}</Chip.Content>
          </Chip>
        )}
      <SummarizeAction
        result={activity.target}
        summaryQnAType={SummaryQnAEnum.ACTIVITY}
        entrypoint={ChatEntryPoint.activity_feed_summary}
        resultPosition={index!}
        // the SERP CTA is not shown in the feed so we don't need to worry about this here
        resultPositionNoCta={index!}
        summarizable={activity.target?.summarizable?.[".tag"]}
      />
      <ActivityOverflowMenuAction activity={activity} />
    </Box>
  );
};

enum ActivityOverflowAction {
  COPY_LINK = "copy_link",
  ADD_TO_STACK = "add_to_stack",
}

const ActivityOverflowMenuAction = ({ activity }: ActivityActionBarProps) => {
  const navigate = useNavigate();
  const { reportPapEvent } = useMirageAnalyticsContext();
  const showStackChooserModal = useShowStackChooserModal(navigate);

  const title = activity.target?.name || "";
  const url = activity.target?.url || "";

  const addToStack = useCallback(() => {
    showStackChooserModal({
      searchTerm: title,
      metadataTitle: title,
      url: url,
      callback: () => {
        // TODO: pap event?
        publishEvent(SurveyEvent.AddStackItem, {}, true);
      },
    });
  }, [title, url, showStackChooserModal]);

  const copyLink = useCallback(() => {
    copyToClipboard(url);
    showSnackbar({ title: i18n.t("copied_link_to_clipboard") });
  }, [url]);

  // using onSelection instead of onClick inside each menu item to get free handling of keyboard events
  const onSelection = useCallback(
    (value: ActivityOverflowAction) => {
      reportPapEvent(
        PAP_Select_StartPageAction({
          actionSurfaceComponent: "activity_feed",
          featureLine: "activity_feed",
          startPageAction: value,
        }),
      );

      switch (value) {
        case ActivityOverflowAction.COPY_LINK:
          copyLink();
          break;
        case ActivityOverflowAction.ADD_TO_STACK:
          addToStack();
          break;
      }
    },
    [copyLink, addToStack, reportPapEvent],
  );

  return (
    <Menu.Wrapper
      shouldPropagateClickOutsideMouseEvents
      closeOnSelection={true}
      onSelection={onSelection}
      onToggle={({ isOpen }) => {
        if (isOpen) {
          reportPapEvent(
            PAP_Select_StartPageAction({
              actionSurfaceComponent: "activity_feed",
              featureLine: "activity_feed",
              startPageAction: "click_overflow_menu",
            }),
          );
        }
      }}
    >
      {({ getContentProps, getTriggerProps }) => (
        <>
          <IconButton
            {...getTriggerProps()}
            tone="neutral"
            variant="borderless"
            aria-label={i18n.t("more")}
          >
            <Box color="Text Subtle" as={UIIcon} src={MoreHorizontalLine} />
          </IconButton>
          <Menu.Content {...getContentProps()}>
            <Menu.Segment>
              <Menu.ActionItem
                value={ActivityOverflowAction.COPY_LINK}
                withTitle={i18n.t("copy_link")}
                withLeftAccessory={<UIIcon src={LinkLine} />}
              />
              <Menu.ActionItem
                value={ActivityOverflowAction.ADD_TO_STACK}
                withTitle={i18n.t("add_to_stack")}
                withLeftAccessory={<UIIcon src={CreateStackLine} />}
                onClick={addToStack}
              />
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};
